import { langFallback$Fetch } from '~/lang/utils/langfetch'
import { CreateMany } from '../../../framework/repositories/interfaces/Write'
import { Image, ImageStatus } from '../../domain/types'
import {
  ExtendedFileMetadata,
  FilestackImagesWithDimensions,
} from '../../types'

type PhotoStatus = 'inprogress' | 'public'
interface Photo {
  id: number
  status: PhotoStatus
  width: number
  height: number
  remote_image: string
  size: number
}
interface Event {
  photosUploaded: {
    photos: Photo[]
  }
}
interface Response {
  success: boolean
  events: Event[]
  message?: string
}

const IMAGE_MINIMUM_LONG_EDGE = 1800
const IMAGE_MINIMUM_SHORT_EDGE = 1350

export class FetchFilestackImageRepository implements CreateMany<any, Image> {
  constructor(
    protected campsiteSlug: string,
    protected token: string,
    protected langCode: string,
  ) {}

  get IMAGE_MAXIMUM_FILE_SIZE_IN_BYTES() {
    return Number(useRuntimeConfig().public.cloudinaryMaximumFileSizeInBytes)
  }

  async createMany(
    filestackImages: FilestackImagesWithDimensions,
  ): Promise<Image[]> {
    // Validate token is present - backend now requires tokens for all photo uploads
    if (!this.token) {
      throw new Error('Token is required for photo upload')
    }
    const filteredImages = {
      filesUploaded: this.filterValidImages(filestackImages.filesUploaded),
    }
    const invalidImages = this.filterInvalidImages(
      filestackImages.filesUploaded,
    )
    const response = await langFallback$Fetch<Response>(
      this.langCode,
      `/_/endpoints/upload-review-photos/${this.campsiteSlug}/${this.token}/`,
      {
        body: JSON.stringify(filteredImages),
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'X-ELDARION-AJAX': 'true',
          'X-Requested-With': 'XMLHttpRequest',
        },
      },
    )
    return [
      ...this.getDataFromInvalidImages(invalidImages),
      ...this.getImagesFromResponse(response),
    ]
  }

  private filterValidImages(images: ExtendedFileMetadata[]) {
    return images.filter((image) =>
      this.isImageValid(image.width, image.height, image.size),
    )
  }

  private filterInvalidImages(images: ExtendedFileMetadata[]) {
    return images.filter(
      (image) => !this.isImageValid(image.width, image.height, image.size),
    )
  }

  private isImageValid(width, height, size) {
    const validPortrait =
      width >= IMAGE_MINIMUM_SHORT_EDGE && height >= IMAGE_MINIMUM_LONG_EDGE
    const validLandscape =
      width >= IMAGE_MINIMUM_LONG_EDGE && height >= IMAGE_MINIMUM_SHORT_EDGE
    const validDimensions = validPortrait || validLandscape
    return validDimensions && size <= this.IMAGE_MAXIMUM_FILE_SIZE_IN_BYTES
  }

  private getDataFromInvalidImages(images: ExtendedFileMetadata[]): Image[] {
    return images.map((image) => ({
      id: image.uploadId,
      url: image.url,
      caption: '',
      status: 'invalid',
      width: image.width,
      height: image.height,
      size: image.size,
    }))
  }

  private getImagesFromResponse(response: Response): Image[] {
    return response.events.reduce((accumulator, event) => {
      accumulator.push(
        ...event.photosUploaded.photos.map((photo) => ({
          id: String(photo.id),
          url: photo.remote_image,
          caption: '',
          status: this.getImageStatus(photo.status),
          width: photo.width,
          height: photo.height,
          size: photo.size,
        })),
      )
      return accumulator
    }, [] as Image[])
  }

  private getImageStatus(status: PhotoStatus): ImageStatus {
    return {
      inprogress: 'processing',
      public: 'processed',
    }[status] as ImageStatus
  }
}
